import BasicModal from '../common/BasicModal';
import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Box, Button, Checkbox, FormControl, FormControlLabel, Radio, RadioGroup, Snackbar, Typography } from '@mui/material';
import { formatNumberWithComma } from '../../utils/number';
import { FinanceContext } from '../../contexts/FinanceContext.tsx';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import IResponse from '../../interfaces/response.ts';
import { IIncreaseBalance } from '../../interfaces/finance.ts';
import { UserContext } from '../../contexts/UserContext.tsx';
import { calculateAddedAmount, calculateBaseAmount } from '../../utils/calculateFinalPrice.ts';

type IPaymentModal = {
	open: boolean;
	onClose: () => void;
	amount: number | null;
	rout: string;
	data: { [key: string]: any };
	handleResultServer?: () => void;
	enableType: boolean;
	showFineDetails?: boolean;
	link?: string;
	isStudio?: boolean;
};

type paymentType = 'wallet' | 'online';

const PaymentModal: FC<IPaymentModal> = ({ showFineDetails = false, open, onClose, amount, isStudio = false, rout, data, handleResultServer = () => {}, enableType, link = '/campaigns' }) => {
	const [openToast, setOpenToast] = useState(false);
	const { t } = useTranslation();
	const [isTermsAccepted, setIsTermsAccepted] = useState(false);
	let navigate = useNavigate();
	const { fetchedGateWays } = useContext(FinanceContext);
	const { userBalance, setUserBalance } = useContext(UserContext);
	const { user } = useContext(UserContext);
	const [price, setPrice] = useState<number>(0);
	const [selectedGateId, setSelectedGateId] = useState<number | null>(null);
	const [type, setType] = useState<paymentType | undefined>();

	const balance = formatNumberWithComma(userBalance);

	const handleChangeType = (event: ChangeEvent<HTMLInputElement>) => {
		setType((event.target as HTMLInputElement).value as paymentType);
	};
	useEffect(() => {
		if (isStudio && data['studios'][0]) {
			axios({
				url: `/studio/showRequest/${data['studios'][0]}`,
			}).then(res => {
				if (res.data.success) {
					setPrice(res.data.data.factor_price);
				}
			});
		}
	}, [data]);

	// const calcPriceWithPercent = calculateFinalPrice()
	const finalAmount = isStudio && price !== 0 ? price! : amount!;

	const terms = t('terms');

	const resetState = () => {
		setIsTermsAccepted(false);
		setSelectedGateId(null);
	};

	const handleTermsChange = (event: any) => {
		setIsTermsAccepted(event.target.checked);
	};

	const handleGateSelection = (gateId: number) => () => {
		setSelectedGateId(gateId);
	};

	const handleCancel = () => {
		resetState();
		onClose();
	};

	function reFetch_wallet() {
		axios<IResponse<number>>({
			url: '/accounting-profile/user-balance',
		}).then(res => {
			if (res.data.success) {
				setUserBalance(res.data.data);
			}
		});
	}

	const handlePay = () => {
		axios<IResponse<IIncreaseBalance>>({
			url: rout,
			method: 'POST',
			data: {
				...data,
				payment_type: type,
				gateway: selectedGateId,
			},
		}).then(res => {
			if (res.data.success) {
				reFetch_wallet();
				setOpenToast(true);
				if (type === 'wallet') {
					navigate(link);
				}
				if (res?.data?.data?.redirect?.method?.toUpperCase() == 'GET' && res?.data?.data?.redirect?.url) {
					window.location.href = res?.data?.data?.redirect?.url;
					return;
				}

				if (res?.data?.data?.redirect?.method?.toUpperCase() === 'POST' && res?.data?.data?.redirect?.url && Array.isArray(res?.data?.data?.redirect?.fields)) {
					const form = document.createElement('form');
					form.method = 'POST';
					form.action = res?.data?.data?.redirect?.url;
					for (const field of res?.data?.data?.redirect?.fields) {
						const input = document.createElement('input');
						input.type = 'hidden';
						input.name = field.name;
						input.value = field.value.toString();
						form.appendChild(input);
					}
					document.body.appendChild(form);
					form.submit();
					handleResultServer();
					onClose();
				}
			}
		});
	};

	useEffect(() => {
		if (!open) {
			resetState();
		}
	}, [open]);

	const assetsBaseUrl = import.meta.env.VITE_BASE_URL_ASSETS;

	const isEnabledSubmit = (type === 'wallet' && isTermsAccepted) || (isTermsAccepted && (selectedGateId || user?.is_admin) && (type || !enableType));

	const percentages = [20, 10];

	const baseAmount = calculateBaseAmount(finalAmount, percentages);
	const serviceAdded = calculateAddedAmount(finalAmount, percentages, 0);
	const taxAdded = calculateAddedAmount(finalAmount, percentages, 1);
	return (
		<>
			<Snackbar open={openToast} autoHideDuration={6000}>
				<Alert severity="success" variant="filled" sx={{ width: '100%' }}>
					{t('payment_has_been_made_successfully')}
				</Alert>
			</Snackbar>
			<BasicModal className="mx-10 h-auto w-full sm:mx-20" open={open} onClose={onClose} title={t('payment')} showBackIcon={false}>
				<div className="flex w-full flex-col justify-between gap-x-4 px-4 pt-8 sm:px-8">
					<div className="mt-3 w-full rounded-lg bg-grey-100 px-4 pb-6 pt-4 sm:px-6">
						<div className="flex flex-col justify-between sm:flex-row">
							<Typography>{t('payable_price')}</Typography>
							<Box className="flex flex-col items-end">
								<div className="flex flex-col items-end">
									{showFineDetails && (
										<>
											<Typography>مبلغ پایه: {baseAmount?.toFixed() ?? baseAmount} تومان</Typography>

											<Typography>+20% سرویس: {serviceAdded?.toFixed() ?? serviceAdded} تومان</Typography>
										</>
									)}

									<Typography>+10% مالیات: {taxAdded?.toFixed() ?? taxAdded} تومان</Typography>
									<Typography>مبلغ نهایی: {finalAmount?.toFixed() ?? finalAmount} تومان</Typography>
								</div>
							</Box>
						</div>
					</div>

					<div className="mt-4 w-full rounded-lg bg-grey-100 px-4 pb-6 pt-4 sm:px-6">
						<Typography className="mb-2 font-medium">{t('select_payment_gate')}</Typography>
						<div className="flex-wra my-1 flex gap-3">
							{fetchedGateWays?.map((gateway, i) => {
								const selectedGateWay = selectedGateId === gateway?.id;

								return (
									<div key={i} onClick={handleGateSelection(gateway?.id)} className={`h-16 w-16 cursor-pointer rounded-lg border p-2 ${selectedGateWay ? 'border-2 border-primary-main' : 'border-grey-500'}`}>
										<img src={assetsBaseUrl + gateway?.image} alt={gateway?.title} className="h-full w-full" />
									</div>
								);
							})}
						</div>
					</div>

					{enableType && (
						<Box className="mt-4 w-full rounded-lg bg-grey-100 px-4 pb-6 pt-4 sm:px-6">
							<Typography className="mb-3 font-medium">{t('payment_method')}</Typography>
							<FormControl>
								<RadioGroup value={type} onChange={handleChangeType}>
									<Box className="flex flex-row gap-4">
										<Box className={`rounded border py-1 ps-5 ${type === 'online' ? 'border-primary-main' : 'border-gray-500'}`}>
											<FormControlLabel value="online" control={<Radio />} label={t('online_payment')} />
										</Box>
										<Box className={`rounded border py-1 ps-5 ${type === 'wallet' ? 'border-primary-main' : 'border-gray-500'}`}>
											<FormControlLabel
												value="wallet"
												control={<Radio />}
												label={
													<>
														{t('wallet_payment')}
														<Typography className={'ms-4 inline text-sm text-grey-600'}>
															<Trans i18nKey="current_balance_label" values={{ balance }} />
														</Typography>
													</>
												}
											/>
										</Box>
									</Box>
								</RadioGroup>
							</FormControl>
						</Box>
					)}

					{/*todo:link & change the color "شرایط و ضوابط"*/}
					<div className="mt-4">
						<FormControlLabel label="" control={<Checkbox checked={isTermsAccepted} onChange={handleTermsChange} className="m-0 text-sm font-medium" />} />
						<Link to={`${import.meta.env.VITE_LANDING}blog/rules`} target={'_blank'} className={'text-primary-main underline'}>
							<Trans i18nKey="accept_terms" values={{ terms }} components={[<Typography className="px-1" />]} />
						</Link>
					</div>

					<div className="mb-8 mt-5 flex w-full flex-col items-center gap-3 sm:flex-row-reverse sm:justify-start">
						<Button variant="outlined" className="w-full border-action-disabledBackground text-action-disabled sm:w-28" onClick={handleCancel}>
							{t('cancel')}
						</Button>
						<Button variant="contained" className="w-full sm:w-60" onClick={handlePay} disabled={!isEnabledSubmit}>
							{t('payment')}
						</Button>
					</div>
				</div>
			</BasicModal>
		</>
	);
};

export default PaymentModal;
